import type { Theme } from "@mui/material";

import { COLOR_SCHEME_ATTRIBUTE } from "@/src/theme/colorSchemeBootstrap";
import { isDarkMode } from "@/src/theme/helpers";
import { themeForScheme } from "@/src/theme/schemeStyles";

/** Scopes a rule to dark mode, following the attribute set before paint. */
const DARK_SCOPE = `:root[${COLOR_SCHEME_ATTRIBUTE}="dark"]`;

const TOAST_SEVERITIES = ["success", "error", "warning", "info"] as const;

type ToastSeverity = (typeof TOAST_SEVERITIES)[number];

const isToastSeverity = (type: string): type is ToastSeverity =>
  TOAST_SEVERITIES.some((severity) => severity === type);

/**
 * Icon colour for a toast of the given react-toastify type.
 *
 * Light mode keeps the filled toast, where the icon sits on the severity colour
 * and inherits the white label. Dark mode toasts are outlined, so the icon
 * carries the Severity/Main accent instead.
 */
export const toastIconColor = (theme: Theme, type: string): string =>
  isDarkMode(theme) && isToastSeverity(type)
    ? theme.palette[type].main
    : "inherit";

/**
 * Dark mode toast styles matching the outlined alert treatment.
 *
 * The dark Severity/Main colours are light tints, so react-toastify's colored
 * theme put white labels on them at roughly 2:1. Dark mode therefore keeps the
 * toast on Background/Paper and spends the severity colour on the border and
 * progress bar only, with the label reading Alert/Content.
 */
const buildOutlinedToastStyles = (theme: Theme): string =>
  TOAST_SEVERITIES.map((severity) => {
    const accentColor = theme.palette[severity].main;

    return `
    ${DARK_SCOPE} .Toastify__toast-theme--colored.Toastify__toast--${severity} {
      background: ${theme.palette.background.paper};
      border: 1px solid ${accentColor};
      color: ${theme.palette.semantic[severity].alertText};
    }
    ${DARK_SCOPE} .Toastify__progress-bar-theme--colored.Toastify__progress-bar--${severity} {
      background: ${accentColor};
    }
  `;
  }).join("");

const buildNotificationVariables = (theme: Theme, scope: string): string => {
  const contrastText = theme.palette.semantic.primary.contrastText;
  const snackbarShadow = theme.shadows[6] ?? theme.shadows[3];

  return `
    ${scope} {
      --toastify-color-success: ${theme.palette.semantic.success.main};
      --toastify-color-error: ${theme.palette.semantic.error.main};
      --toastify-color-warning: ${theme.palette.semantic.warning.main};
      --toastify-color-info: ${theme.palette.semantic.info.main};
      --toastify-color-progress-light: ${contrastText};
      --toastify-color-progress-dark: ${contrastText};
      --toastify-color-progress-info: ${contrastText};
      --toastify-color-progress-success: ${contrastText};
      --toastify-color-progress-warning: ${contrastText};
      --toastify-color-progress-error: ${contrastText};
      --theme-notification-shadow: ${snackbarShadow};
      --theme-notification-close-tap-highlight: ${theme.palette.action.selected};
      --theme-notification-close-hover-bg: ${theme.palette.divider};
      --theme-notification-close-focus-outline: ${theme.palette.text.secondary};
      --theme-notification-close-active-bg: ${theme.palette.action.disabled};
    }
  `;
};

/**
 * Toast colors for both schemes, keyed on the scheme attribute.
 *
 * The container is part of every prerendered page, so it carries both sets
 * rather than the active scheme's; otherwise the static HTML would hold the
 * light colors until React hydrated.
 */
export const buildNotificationThemeStyles = (theme: Theme): string => {
  const darkTheme = themeForScheme(theme, "dark");

  return [
    buildNotificationVariables(themeForScheme(theme, "light"), ":root"),
    buildNotificationVariables(darkTheme, DARK_SCOPE),
    buildOutlinedToastStyles(darkTheme)
  ].join("");
};
